// material-purchase.ts — Real purchase of an in-house stock (UPZ) material.
//
// Replaces the quote's material estimate with the actual purchase. A material is consumed in its
// packaging unit: linear metres for flexible (reel cut across its web width), whole sheets for rigid.
// Consumption comes from `prodRecap` (technical margins, nesting and waste coefficient included) and
// is never recomputed here. An incomplete purchase is `null`, never 0, and the purchase REPLACES the
// estimate instead of adding to it.
//
// Pure module: no database reads, no React dependency.

import { num, round2, round3 } from "../core/core";
import { parseFormat, linearLengthPerPiece, piecesPerSheet, ZERO_MARGINS, type TechnicalMargins } from "../ops/material-format";

/** Packaging decides the unit — and therefore the whole UI vocabulary. */
export type PurchaseKind = "flexible" | "rigid";

/** What the user enters for ONE material. Dictionary key = material ref. */
export interface MaterialPurchase {
  /** Who we buy from. Free text: may be absent from the directory. */
  supplier?: string;
  /** Negotiated purchase price, €/m². Prefills from `MATERIAL_REFS.pa`, editable. */
  pricePerM2?: string | number;
  /**
   * The lever. Flexible: reel length kept, in linear metres. Rigid: number of sheets kept.
   * Empty ⇒ packaging default (see `retainedQuantity`). Decimals allowed even on sheets, so a
   * half-sheet can be charged to a quote when stock lives at the workshop.
   */
  quantity?: string | number;
}

/** What the quote CONSUMES, as already computed by `prodRecap`. Never recomputed here. */
export interface MaterialNeed {
  nature: PurchaseKind;
  /** Flexible: the WEB WIDTH in m. Rigid: the AREA OF ONE SHEET in m². The conversion factor. */
  conversionFactor: number;
  /** Consumption INCLUDING WASTE: linear metres for flexible, sheets for rigid. */
  consumed: number;
  /** Packaging default quantity: format length (flexible), `⌈consumed⌉` (rigid). */
  defaultQuantity: number;
}

/**
 * Authoritative quantity: the entered value if any, otherwise the packaging default.
 * @param purchase The user's purchase entry for the material.
 * @param need The material need.
 * @returns The retained quantity (linear metres or sheets).
 */
export function retainedQuantity(purchase: MaterialPurchase | undefined, need: MaterialNeed): number {
  const q = num(purchase?.quantity);
  return q > 0 ? round3(q) : round3(need.defaultQuantity);
}

/**
 * Area actually purchased, in m² — this is what the price multiplies.
 * @param purchase The user's purchase entry for the material.
 * @param need The material need.
 * @returns The purchased area in m², or null when it cannot be established.
 */
export function purchasedSurface(purchase: MaterialPurchase | undefined, need: MaterialNeed): number | null {
  const q = retainedQuantity(purchase, need);
  return need.conversionFactor > 0 && q > 0 ? round3(q * need.conversionFactor) : null;
}

/**
 * FINAL COST of the material for this quote. `null` while the price is missing: a half-entered
 * purchase is not a €0 purchase, and the quote must keep showing its estimate.
 * @param purchase The user's purchase entry for the material.
 * @param need The material need.
 * @returns The final cost in euros, or null while the purchase is incomplete.
 */
export function finalCost(purchase: MaterialPurchase | undefined, need: MaterialNeed): number | null {
  const surface = purchasedSurface(purchase, need), unitPrice = num(purchase?.pricePerM2);
  return surface != null && unitPrice > 0 ? round2(surface * unitPrice) : null;
}

/** What the quote footer SHOWS, so a crushed margin reads as a fact rather than a mistake. */
export interface MaterialBalance {
  nature: PurchaseKind;
  /** Quantity kept: linear metres (flexible) or sheets (rigid). */
  purchased: number;
  /** Consumption including waste, same unit. */
  consumed: number;
  /**
   * Purchased − consumed. **May be NEGATIVE**: less was kept than is consumed, so the entry needs
   * fixing. Clamping to zero would hide exactly the case worth seeing.
   */
  balance: number;
  /** The balance converted to m², because "half a sheet" does not say how much that is. */
  balanceM2: number;
  insufficient: boolean;
}

/**
 * Purchased-versus-consumed balance of a material.
 * @param purchase The user's purchase entry for the material.
 * @param need The material need.
 * @returns The balance, or null when the conversion factor is unknown.
 */
export function materialBalance(purchase: MaterialPurchase | undefined, need: MaterialNeed): MaterialBalance | null {
  if (!(need.conversionFactor > 0)) return null;
  const purchased = retainedQuantity(purchase, need), consumed = round3(need.consumed);
  const balance = round3(purchased - consumed);
  return { nature: need.nature, purchased, consumed, balance, balanceM2: round3(balance * need.conversionFactor), insufficient: balance < 0 };
}

/**
 * COST SPLIT ACROSS THE LINES, pro rata of what each one consumes.
 *
 * The parts must sum to the total TO THE CENT: the first n−1 are rounded and **the last absorbs the
 * remainder**. The TOTAL is authoritative and serves as denominator — never the sum of per-line
 * values, which rounds once per line and drifts.
 *
 * Edge case: no consumption at all (zero denominator) puts the whole cost on the FIRST line — the
 * purchase exists and must weigh somewhere.
 * @param total The total cost to split.
 * @param consumptionPerLine The consumption of each line.
 * @returns The cost share of each line, summing to the total.
 */
export function allocateProRata(total: number, consumptionPerLine: readonly number[]): number[] {
  const n = consumptionPerLine.length;
  if (n === 0) return [];
  if (n === 1) return [round2(total)];
  const sum = consumptionPerLine.reduce((a, m) => a + m, 0);
  if (sum <= 0) return consumptionPerLine.map((_, i) => (i === 0 ? round2(total) : 0));
  const parts: number[] = [];
  let cumulated = 0;
  for (let i = 0; i < n - 1; i++) {
    const p = round2((total * consumptionPerLine[i]!) / sum);
    parts.push(p);
    cumulated = round2(cumulated + p);
  }
  parts.push(round2(total - cumulated)); // the last absorbs the remainder — the sum matches the total, to the cent
  return parts;
}

/** ONE quote line, reduced to what imposition needs. */
export interface PurchasePiece {
  L: number;
  H: number;
  qty: number;
  /** Raw material neutralisation (§28): geometry on the RAW format, without technical margins. */
  raw: boolean;
}

/**
 * MATERIAL NEED, derived from the lines that consume it.
 *
 * This aggregation also exists in `prodRecap` (same geometry and margins, summed separately); the
 * divergence is pinned by the `material-purchase-vs-summary` test.
 *
 * Two deliberate differences with the summary, both for precision: flexible keeps the full linear-metre
 * value instead of the summary's 2-decimal display rounding, and rigid keeps the fractional sheet count
 * so the sheet balance is not always zero.
 *
 * `null` when the format is unreadable or the nature unknown — the purchase then has no effect and
 * the quote keeps its estimate.
 * @param pieces The lines that consume the material.
 * @param o The nature, the material format, the waste coefficient and the technical margins.
 * @returns The material need and the consumption of each line, or null.
 */
export function needFromPieces(
  pieces: readonly PurchasePiece[],
  o: { nature: PurchaseKind; format: string | null; coeff: number; margins: TechnicalMargins },
): { need: MaterialNeed; consumptionPerLine: number[] } | null {
  const dims = parseFormat(o.format);
  if (!dims) return null;
  const [width, length] = dims;
  const consumptionPerLine: number[] = [];

  if (o.nature === "flexible") {
    let mmTotal = 0;
    for (const p of pieces) {
      const m = p.raw ? ZERO_MARGINS : o.margins;
      const mm = p.L > 0 && p.H > 0 && p.qty > 0
        ? linearLengthPerPiece(width - 2 * m.webMargin, length - 2 * m.reelMargin, p.L, p.H, p.qty, m.pieceGap)
        : 0;
      consumptionPerLine.push(mm == null ? 0 : round3((mm / 1000) * (1 + o.coeff)));
      mmTotal += mm ?? 0;
    }
    const consumed = round3((mmTotal / 1000) * (1 + o.coeff));
    // Default: the format LENGTH — that is the reel being opened.
    return { need: { nature: "flexible", conversionFactor: round3(width / 1000), consumed, defaultQuantity: round3(length / 1000) }, consumptionPerLine };
  }

  let sheets = 0;
  for (const p of pieces) {
    const m = p.raw ? ZERO_MARGINS : o.margins;
    const perSheet = p.L > 0 && p.H > 0 && p.qty > 0
      ? piecesPerSheet(width - 2 * m.sheetMargin, length - 2 * m.sheetMargin, p.L, p.H, m.pieceGap)
      : 0;
    const n = perSheet >= 1 ? Math.ceil(p.qty / perSheet) : 0;
    consumptionPerLine.push(round3(n * (1 + o.coeff)));
    sheets += n;
  }
  const consumed = round3(sheets * (1 + o.coeff));
  // Default: you cannot buy half a sheet — packaging rounds up.
  return {
    need: { nature: "rigid", conversionFactor: round3((width * length) / 1e6), consumed, defaultQuantity: Math.ceil(consumed) },
    consumptionPerLine,
  };
}
